import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { withApollo } from 'react-apollo';
import moment from 'moment-timezone';
import { REFRESH_GOOGLE_TOKEN } from '../gql/bookingapi';
import {
  getGoogleCalendarConnectionData,
  saveGoogleCalendarConnectionData,
  isGoogleCalendarConnected,
  isGoogleCalendarTokenExpired,
  clearGoogleCalendarData,
} from '../utils/store/googleCalendarPersistence';
import { GOOGLE_CALENDAR_API_URL } from '../constants';

const GoogleCalendarContext = createContext();

export const useGoogleCalendar = () => {
  const context = useContext(GoogleCalendarContext);
  if (!context) {
    throw new Error('useGoogleCalendar must be used within a GoogleCalendarProvider');
  }
  return context;
};

export const GoogleCalendarProvider = withApollo(({ children, client }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [selectedCalendarIds, setSelectedCalendarIds] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [googleEvents, setGoogleEvents] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [error, setError] = useState(null);
  const [isMounted, setIsMounted] = useState(true);


  const onError = useCallback((resp) => {
    console.error('Google Calendar API error response:', resp);
    const errorMessage = resp.error ? resp.error : resp.result?.error?.message || 'Unknown error occurred';
    console.error('GoogleCalendarProvider', 'Google Calendar API error:', {
      error: errorMessage,
      response: resp,
      fullError: resp.result?.error
    });
    if (isMounted) {
      setError(errorMessage);
    }
  }, [isMounted]);

  /**
   * Check if we can attempt token refresh or need full reconnection
   * Now returns true even without refresh token to handle cross-device login
   */
  const canAttemptTokenRefresh = useCallback(() => {
    // Always allow token refresh attempt - if no refresh token,
    // server will provide new tokens for authenticated users
    return true;
  }, []);

  /**
   * Refresh Google access token using the stored refresh token
   */
  const refreshAccessToken = useCallback(async () => {
    try {
      const connectionData = getGoogleCalendarConnectionData();

      // If no refresh token, pass empty string to get new tokens from server
      // This handles the case where user is logged in on different device
      const refreshToken = connectionData.refreshToken || "";

      const response = await client.mutate({
        mutation: REFRESH_GOOGLE_TOKEN,
        variables: {
          action: "refresh",
          refresh_token: refreshToken
        },
        fetchPolicy: 'no-cache',
      });

      const data = response?.data?.connectGoogleCalendar;
      if (data?.error) {
        // Check if error indicates invalid token
        const errorLower = data.error.toLowerCase();
        if (errorLower.includes('invalid') || errorLower.includes('expired') || errorLower.includes('revoked')) {
          // Clear tokens immediately to prevent retry loops
          try {
            clearGoogleCalendarData();
            sessionStorage.removeItem('googleCalendar_selectedUsers');
            
            // Dispatch storage event to notify other components (like the toggle)
            window.dispatchEvent(new Event('storage'));
          } catch (storageError) {
            console.warn('Failed to clear storage:', storageError);
          }
          
          if (isMounted) {
            setIsLoggedIn(false);
            setCalendars([]);
            setSelectedCalendarIds([]);
            setCalendarData({});
            setError('Failed to load Google Calendar. Please reconnect.');
          }
          
          const authError = new Error('Failed to load Google Calendar. Please reconnect.');
          authError.isAuthError = true;
          throw authError;
        }
        throw new Error(data.error);
      }

      // Save updated token data
      const updatedConnectionData = {
        calendarId: data.calendarId || connectionData.calendarId || 'primary',
        accessToken: data.accessToken,
        refreshToken: data.refreshToken || connectionData.refreshToken,
        expiryDate: data.expiryDate,
        tokenType: data.tokenType || connectionData.tokenType || 'Bearer',
        scope: data.scope || connectionData.scope || 'https://www.googleapis.com/auth/calendar.readonly',
      };

      saveGoogleCalendarConnectionData(updatedConnectionData);

      return data.accessToken;
    } catch (error) {
      console.error('Failed to refresh Google Calendar access token:', error);

      // If it's already an auth error, just rethrow
      if (error.isAuthError) {
        throw error;
      }

      // If the refresh mutation doesn't exist or fails, clear the connection
      // and ask user to reconnect
      if (isMounted) {
        setError('Failed to load Google Calendar. Please reconnect.');
        setIsLoggedIn(false);
      }

      // Clear stored tokens since they're invalid
      try {
        clearGoogleCalendarData();
        sessionStorage.removeItem('googleCalendar_selectedUsers');
        
        // Dispatch storage event to notify other components (like the toggle)
        window.dispatchEvent(new Event('storage'));
      } catch (storageError) {
        console.warn('Failed to clear storage:', storageError);
      }

      // Mark as auth error to prevent retries
      const authError = new Error('Failed to load Google Calendar. Please reconnect.');
      authError.isAuthError = true;
      throw authError;
    }
  }, [client, isMounted]);

  /**
   * Ensure we have a valid access token before making API calls
   */
  const ensureValidToken = useCallback(async () => {
    const connectionData = getGoogleCalendarConnectionData();

    if (!connectionData.accessToken) {
      const authError = new Error('No access token available. Please connect to Google Calendar.');
      authError.isAuthError = true;
      throw authError;
    }

    // Check if token is expired
    if (isGoogleCalendarTokenExpired()) {
      // Check again if we still have a refresh token before attempting refresh
      const currentData = getGoogleCalendarConnectionData();
      if (!currentData.accessToken && !currentData.refreshToken) {
        // Tokens were cleared (likely due to auth error), don't retry
        const authError = new Error('Failed to load Google Calendar. Please reconnect.');
        authError.isAuthError = true;
        throw authError;
      }
      return await refreshAccessToken();
    }

    return connectionData.accessToken;
  }, [refreshAccessToken]);

  /**
   * Make authenticated request to Google Calendar API V3
   */
  const makeGoogleAPIRequest = useCallback(async (endpoint, options = {}) => {
    try {
      const accessToken = await ensureValidToken();

      const headers = {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        ...options.headers,
      };

      const response = await fetch(`${GOOGLE_CALENDAR_API_URL}${endpoint}`, {
        ...options,
        headers,
      });

      if (!response.ok) {
        // Check for authentication errors (401 Unauthorized or 403 Forbidden)
        if (response.status === 401 || response.status === 403) {
          console.error('Google Calendar authentication failed. Clearing invalid tokens.');
          
          // Clear stored tokens immediately
          try {
            clearGoogleCalendarData();
            sessionStorage.removeItem('googleCalendar_selectedUsers');
            
            // Dispatch storage event to notify other components (like the toggle)
            window.dispatchEvent(new Event('storage'));
          } catch (storageError) {
            console.warn('Failed to clear storage:', storageError);
          }
          
          // Update state to reflect disconnection
          if (isMounted) {
            setIsLoggedIn(false);
            setCalendars([]);
            setSelectedCalendarIds([]);
            setCalendarData({});
            setError('Failed to load Google Calendar. Please reconnect.');
          }
          
          // Throw a specific error that won't trigger retries
          const authError = new Error('Google Calendar authentication failed. Please reconnect.');
          authError.isAuthError = true;
          throw authError;
        }
        
        const errorText = await response.text();
        throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Google Calendar API request failed:', error);
      throw error;
    }
  }, [ensureValidToken, isMounted]);

  /**
   * Get list of user's calendars from Google Calendar API V3
   */
  const loadGoogleCalendars = useCallback(async () => {
    try {
      setError(null);
      const data = await makeGoogleAPIRequest('/users/me/calendarList');

      const googleCalendars = data.items?.map(calendar => ({
        id: calendar.id,
        summary: calendar.summary || calendar.summaryOverride,
        description: calendar.description,
        backgroundColor: calendar.backgroundColor || '#1976d2',
        primary: calendar.primary || false,
        accessRole: calendar.accessRole,
        selected: calendar.selected,
      })) || [];

      // Sort calendars with primary first
      googleCalendars.sort((a, b) => (b.primary ? 1 : 0) - (a.primary ? 1 : 0));

      if (isMounted) {
        setCalendars(googleCalendars);
      }

      return googleCalendars;
    } catch (error) {
      console.error('Failed to load Google calendars:', error);
      
      // Re-throw auth errors so calling code can handle them
      if (error.isAuthError) {
        throw error;
      }
      
      // Call onError for non-auth errors
      onError(error);
      
      return [];
    }
  }, [makeGoogleAPIRequest, onError, isMounted]);

  /**
   * Load free/busy information from Google Calendar API V3 FreeBusy endpoint
   */
  const loadGoogleCalendarFreeBusy = useCallback(async (calendarIds, startDate, endDate) => {
    try {
      const timeMin = new Date(startDate).toISOString();
      const timeMax = new Date(endDate).toISOString();

      const requestBody = {
        timeMin,
        timeMax,
        items: calendarIds.map(id => ({ id }))
      };

      const data = await makeGoogleAPIRequest('/freeBusy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      // Transform FreeBusy data to a format compatible with calendar view
      const busySlots = [];

      if (data.calendars) {
        Object.entries(data.calendars).forEach(([calendarId, calendarData]) => {
          if (calendarData.busy) {
            calendarData.busy.forEach(busySlot => {
              busySlots.push({
                id: `busy-${calendarId}-${busySlot.start}-${busySlot.end}`,
                title: 'Busy',
                start: busySlot.start,
                end: busySlot.end,
                allDay: false,
                googleCalendarId: calendarId,
                resource: calendarId,
                isBusySlot: true, // Flag to identify this as a busy slot
                status: 'busy'
              });
            });
          }

          // Log any errors for this calendar
          if (calendarData.errors) {
            console.warn(`Errors for calendar ${calendarId}:`, calendarData.errors);
          }
        });
      }

      return busySlots;
    } catch (error) {
      console.error(`Failed to load FreeBusy data for calendars:`, error);
      // Return empty array instead of failing completely
      return [];
    }
  }, [makeGoogleAPIRequest]);

  // Update URL parameters with selected calendars
  const updateUrlWithSelectedCalendars = useCallback((calendarIds) => {
    const url = new URL(window.location);
    // Ensure we don't write duplicate IDs into the URL
    const ids = Array.from(new Set(calendarIds || []));
    if (ids.length > 0) {
      url.searchParams.set('selectedCalendars', ids.join(','));
    } else {
      url.searchParams.delete('selectedCalendars');
    }
    window.history.replaceState({}, '', url);
  }, []);

  // Load selected calendars from URL parameters
  const loadSelectedCalendarsFromUrl = useCallback((availableCalendars) => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlCalendars = urlParams.get('selectedCalendars');

      if (urlCalendars && availableCalendars.length > 0) {
  // split and dedupe incoming ids from URL
  const rawIds = urlCalendars.split(',').map(id => id.trim()).filter(Boolean);
  const calendarIds = Array.from(new Set(rawIds));
        // Validate that these calendar IDs exist in the available calendars
        const validCalendarIds = calendarIds.filter(id =>
          availableCalendars.some(cal => cal.id === id)
        );

        if (validCalendarIds.length > 0) {
          // Update calendar data to mark these as checked
          const updatedCalendarData = {};
          availableCalendars.forEach(cal => {
            updatedCalendarData[cal.id] = {
              name: cal.summary,
              color: cal.backgroundColor,
              checked: validCalendarIds.includes(cal.id)
            };
          });

          if (isMounted) {
            setCalendarData(updatedCalendarData);
            const deduped = Array.from(new Set(validCalendarIds));
            setSelectedCalendarIds(deduped);
          }
          // If the URL originally contained duplicates or invalid ids, normalize it
          if (rawIds.join(',') !== calendarIds.join(',')) {
            // replace URL with normalized list
            updateUrlWithSelectedCalendars(Array.from(new Set(validCalendarIds)));
          }

          return validCalendarIds;
        }
      }
    } catch (error) {
      console.warn('Failed to load selected calendars from URL:', error);
    }
    return null;
  }, [isMounted, updateUrlWithSelectedCalendars]);

  const loadSelectedUsersFromStorage = useCallback(() => {
    try {
      // First check URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const urlUsers = urlParams.get('googleCalendarUsers');

      if (urlUsers) {
        const userIds = urlUsers.split(',');
        // Convert user IDs back to user objects - this will need to be enhanced
        // when we have actual user data
        const usersFromUrl = userIds.map(id => ({ userId: id, name: id }));
        if (isMounted) {
          setSelectedUsers(usersFromUrl);
        }
        sessionStorage.setItem('googleCalendar_selectedUsers', JSON.stringify(usersFromUrl));
        return usersFromUrl;
      }

      // Fallback to session storage
      const stored = sessionStorage.getItem('googleCalendar_selectedUsers');
      if (stored) {
        const users = JSON.parse(stored);
        if (isMounted) {
          setSelectedUsers(users);
        }
        return users;
      }
    } catch (error) {
      console.warn('Failed to load selected users from storage:', error);
    }
    return [];
  }, [isMounted]);

  /**
   * Initialize calendar selection based on URL or default to primary
   */
  const initializeCalendarSelection = useCallback(async (calendars) => {
    try {
      // Try to load calendars from URL first
      const urlSelectedCalendars = loadSelectedCalendarsFromUrl(calendars);

      if (!urlSelectedCalendars) {
        // Fall back to default behavior (select primary calendar)
        const calData = {};
        const primaryCalId = calendars.find(c => c.primary)?.id || calendars[0]?.id;

        for (const c of calendars) {
          calData[c.id] = {
            name: c.summary,
            color: c.backgroundColor,
            checked: c.id === primaryCalId
          };
        }

        if (isMounted) {
          setCalendarData(calData);
        }

        if (primaryCalId) {
          if (isMounted) {
            setSelectedCalendarIds([primaryCalId]);
          }
          // Update URL with the default selection
          updateUrlWithSelectedCalendars([primaryCalId]);
        }
      }

      // Load previously selected users
      loadSelectedUsersFromStorage();
    } catch (error) {
      console.error('Failed to initialize calendar selection:', error);
    }
  }, [loadSelectedCalendarsFromUrl, updateUrlWithSelectedCalendars, loadSelectedUsersFromStorage, isMounted]);

  const signIn = useCallback(async () => {
    if (!isMounted) return Promise.reject(new Error('Component unmounted'));

    setError(null);

    // Check if already connected via stored tokens
    if (isGoogleCalendarConnected()) {
      // If token is expired, try to refresh it first
      if (isGoogleCalendarTokenExpired()) {
        if (canAttemptTokenRefresh()) {
          try {
            await refreshAccessToken();
          } catch (error) {
            console.error('Failed to refresh token during signIn:', error);
            const errorMessage = 'Failed to load Google Calendar. Please reconnect.';
            if (isMounted) {
              setError(errorMessage);
            }
            return Promise.reject(error);
          }
        } else {
          const errorMessage = 'Failed to load Google Calendar. Please reconnect.';
          if (isMounted) {
            setError(errorMessage);
          }
          return Promise.reject(new Error(errorMessage));
        }
      }

      if (isMounted) {
        setIsLoggedIn(true);
      }
      const calendars = await loadGoogleCalendars();
      await initializeCalendarSelection(calendars);
      return Promise.resolve();
    }

    // If no valid connection, user needs to go through OAuth flow
    const errorMessage = 'Please use the Google Calendar toggle to connect your account first.';
    console.warn('GoogleCalendarProvider', errorMessage);
    if (isMounted) {
      setError(errorMessage);
    }
    return Promise.reject(new Error(errorMessage));
  }, [loadGoogleCalendars, initializeCalendarSelection, refreshAccessToken, canAttemptTokenRefresh, isMounted]);

  const signOut = useCallback(() => {
    setError(null);

    // Clear all state including cache
    setIsLoggedIn(false);
    setCalendars([]);
    setSelectedCalendarIds([]);
    setCalendarData({});
      
      // Dispatch storage event to notify other components (like the toggle)
      window.dispatchEvent(new Event('storage'));
    setGoogleEvents([]);
    
    // Clear all Google Calendar data from storage
    try {
      clearGoogleCalendarData();
      sessionStorage.removeItem('googleCalendar_selectedUsers');
    } catch (storageError) {
      console.warn('Failed to clear storage:', storageError);
    }
    
    setSelectedUsers([]);

    // Clear URL parameters
    const url = new URL(window.location);
    url.searchParams.delete('selectedCalendars');
    url.searchParams.delete('googleCalendarUsers');
    window.history.replaceState({}, '', url);
  }, []);



  /**
   * Load free/busy data for multiple calendars efficiently using FreeBusy API
   * Handles chunking for large date ranges (>90 days) automatically
   */
  const loadCalendarEvents = useCallback(async (calendarIds, startDate, endDate) => {
    if (!calendarIds || calendarIds.length === 0) {
      return [];
    }

    try {
      setIsLoading(true);

      // Check if we have valid connection
      if (!isGoogleCalendarConnected()) {
        console.warn('Not connected to Google Calendar');
        return [];
      }

      // Check if date range exceeds 90 days and split if necessary
      const startMoment = moment(startDate);
      const endMoment = moment(endDate);
      const totalDays = endMoment.diff(startMoment, 'days');
      const MAX_DAYS_PER_REQUEST = 90;

      let allBusySlots = [];

      if (totalDays <= MAX_DAYS_PER_REQUEST) {
        // Range is within limit, make single request
        console.log('Making single Google Calendar request');
        allBusySlots = await loadGoogleCalendarFreeBusy(calendarIds, startDate, endDate);
      } else {
        // Split large range into chunks and make async requests
        const dateRanges = [];
        let currentStart = startMoment.clone();

        while (currentStart.isBefore(endMoment)) {
          const currentEnd = moment.min(
            currentStart.clone().add(MAX_DAYS_PER_REQUEST, 'days'),
            endMoment
          );

          dateRanges.push({
            start: currentStart.toDate(),
            end: currentEnd.toDate()
          });

          // Move to next chunk without overlap
          currentStart = currentEnd.clone();
        }

        // Make all requests asynchronously with error handling
        const loadPromises = dateRanges.map(async (range, index) => {
          try {
            return await loadGoogleCalendarFreeBusy(calendarIds, range.start, range.end);
          } catch (error) {
            console.error(`Error loading Google Calendar chunk ${index + 1}:`, error);
            // Return empty array for failed chunks to allow others to complete
            return [];
          }
        });

        const results = await Promise.allSettled(loadPromises);
        const successfulChunks = results
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value)
          .filter(Array.isArray);

        const failedChunks = results.filter(r => r.status === 'rejected').length;
        if (failedChunks > 0) {
          console.warn(`${failedChunks} Google Calendar chunks failed to load`);
        }

        // Combine all busy slots from successful chunks
        allBusySlots = successfulChunks.flat();
      }

      // Set combined results to state only if component is still mounted
      if (isMounted) {
        setGoogleEvents(allBusySlots);
      }

      return allBusySlots;
    } catch (error) {
      console.error('GoogleCalendarProvider', 'Failed to load calendar FreeBusy data:', error);
      
      // Re-throw auth errors so calling code can handle them
      if (error.isAuthError) {
        throw error;
      }
      
      // Call onError for non-auth errors
      onError(error);
      
      return [];
    } finally {
      if (isMounted) {
        setIsLoading(false);
      }
    }
  }, [onError, loadGoogleCalendarFreeBusy, isMounted]);

  const toggleCalendar = useCallback((calendarId, checked) => {
    if (!isMounted) return;

    setCalendarData(prev => ({
      ...prev,
      [calendarId]: {
        ...prev[calendarId],
        checked
      }
    }));

    // Use functional update to avoid stale closure and race conditions
    setSelectedCalendarIds((prevIds) => {
      const nextIds = checked
        ? Array.from(new Set([...(prevIds || []), calendarId]))
        : (prevIds || []).filter(id => id !== calendarId);

      // Update URL with normalized list
      updateUrlWithSelectedCalendars(nextIds);

      return nextIds;
    });
  }, [updateUrlWithSelectedCalendars, isMounted]);

  // Save/load selected users from session storage and URL parameters
  const updateSelectedUsers = useCallback((users) => {
    if (!isMounted) return;

    setSelectedUsers(users);
    sessionStorage.setItem('googleCalendar_selectedUsers', JSON.stringify(users));

    // Update URL parameters
    const url = new URL(window.location);
    if (users.length > 0) {
      const userIds = users.map(user => user.userId || user.user || user.id).filter(Boolean);
      url.searchParams.set('googleCalendarUsers', userIds.join(','));
    } else {
      url.searchParams.delete('googleCalendarUsers');
    }
    window.history.replaceState({}, '', url);
  }, [isMounted]);


  useEffect(() => {
    setIsMounted(true);

    // Listen for storage changes (e.g., when user disconnects via toggle)
    const handleStorageChange = () => {
      const connected = isGoogleCalendarConnected();
      if (!connected && isMounted) {
        // User disconnected - clear all state
        setIsLoggedIn(false);
        setCalendars([]);
        setSelectedCalendarIds([]);
        setCalendarData({});
        setGoogleEvents([]);
        setSelectedUsers([]);
        
        // Clear URL parameters
        const url = new URL(window.location);
        url.searchParams.delete('selectedCalendars');
        url.searchParams.delete('googleCalendarUsers');
        window.history.replaceState({}, '', url);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Check for existing Google Calendar connection on mount
    const checkExistingConnection = async () => {
      try {
        if (isGoogleCalendarConnected()) {
          if (isMounted) {
            setIsLoggedIn(true);
          }

          // Load calendars if we have a valid connection
          if (!isGoogleCalendarTokenExpired()) {
            const calendars = await loadGoogleCalendars();
            if (calendars.length > 0 && isMounted) {
              await initializeCalendarSelection(calendars);
            }
          } else {
            // Token is expired, try to refresh it
            if (canAttemptTokenRefresh()) {
              try {
                await refreshAccessToken();
                const calendars = await loadGoogleCalendars();
                if (calendars.length > 0 && isMounted) {
                  await initializeCalendarSelection(calendars);
                }
              } catch (error) {
                console.error('Failed to refresh token on mount:', error);
                // Don't set error or logged in state if component is unmounted
                if (isMounted && !error.isAuthError) {
                  setError('Failed to load Google Calendar. Please reconnect.');
                  setIsLoggedIn(false);
                }
              }
            } else {
              // No refresh token available, clear connection
              if (isMounted) {
                setError('Failed to load Google Calendar. Please reconnect.');
                setIsLoggedIn(false);
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to check existing Google Calendar connection:', error);
        if (isMounted) {
          setError('Failed to load Google Calendar. Please reconnect.');
        }
      }
    };

    checkExistingConnection();

    // Load selected users on mount
    if (isMounted) {
      loadSelectedUsersFromStorage();
    }

    // Cleanup function
    return () => {
      setIsMounted(false);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [loadGoogleCalendars, initializeCalendarSelection, loadSelectedUsersFromStorage, refreshAccessToken, canAttemptTokenRefresh, isMounted]);

  // If URL doesn't contain selectedCalendars but we already have a selection (e.g. from Autocomplete),
  // write it to the URL so the two stay in sync.
  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlCalendars = urlParams.get('selectedCalendars');

      if (!urlCalendars && selectedCalendarIds && selectedCalendarIds.length > 0) {
        updateUrlWithSelectedCalendars(selectedCalendarIds);
      }
    } catch (err) {
      // ignore URL errors
    }
  }, [selectedCalendarIds, updateUrlWithSelectedCalendars]);

  const value = {
    isLoggedIn,
    isLoading,
    calendars,
    selectedCalendarIds,
    calendarData,
    googleEvents,
    selectedUsers,
    error,
    signIn,
    signOut,
    loadCalendarEvents,
    toggleCalendar,
    updateSelectedUsers,
    setError,
    // Token management
    refreshAccessToken,
    ensureValidToken,
    canAttemptTokenRefresh,
    // Calendar management
    loadGoogleCalendars,
  };

  return (
    <GoogleCalendarContext.Provider value={value}>
      {children}
    </GoogleCalendarContext.Provider>
  );
});
